import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../api/axios";
import { 
  Calendar, 
  Clock, 
  Video, 
  MessageCircle,
  ExternalLink,
  ChevronRight,
  Loader2
} from "lucide-react";

const CALL_METHOD_ICONS = {
  zoom: Video,
  google_meet: Video,
  whatsapp: MessageCircle
};

const CALL_METHOD_LABELS = {
  zoom: "Zoom",
  google_meet: "Google Meet",
  whatsapp: "WhatsApp"
};

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  confirmed: "bg-emerald-100 text-emerald-700",
  completed: "bg-slate-100 text-slate-600",
  cancelled: "bg-red-100 text-red-700"
};

const MyBookings = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const isRTL = i18n.language === 'ar';
  const primaryLanguage = localStorage.getItem('passportTalk_primaryLanguage') || 'en';
  const isEnglishUser = primaryLanguage === 'en';

  useEffect(() => {
    if (!isEnglishUser) {
      setLoading(false);
      return;
    }
    fetchBookings();
  }, [isEnglishUser]);

  const fetchBookings = async () => {
    try {
      const response = await api.get('/bookings/me');
      setBookings(response.data);
    } catch (err) {
      console.error("Failed to load bookings:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return { date: '', time: '' };
    const date = new Date(isoString);
    return {
      date: date.toLocaleDateString(i18n.language, { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      }),
      time: date.toLocaleTimeString(i18n.language, { 
        hour: '2-digit', 
        minute: '2-digit'
      })
    };
  };

  const isUpcoming = (startTime) => {
    return new Date(startTime) > new Date();
  };

  // Don't show for non-English users
  if (!isEnglishUser) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
        <h3 className="text-lg font-semibold text-ocean mb-4">{t('bookings.myBookings')}</h3>
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 text-aviation animate-spin" />
        </div>
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
        <h3 className="text-lg font-semibold text-ocean mb-4">{t('bookings.myBookings')}</h3>
        <p className="text-slate-500 text-sm">{t('bookings.noBookings')}</p>
        <button
          onClick={() => navigate('/agents')}
          className="mt-3 text-aviation text-sm font-medium hover:underline flex items-center gap-1"
        >
          {t('bookings.browseAgents')}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Separate upcoming and past bookings
  const upcomingBookings = bookings.filter(b => isUpcoming(b.start_time_utc) && b.status !== 'cancelled');
  const pastBookings = bookings.filter(b => !isUpcoming(b.start_time_utc) || b.status === 'cancelled');

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <h3 className="text-lg font-semibold text-ocean mb-4">{t('bookings.myBookings')}</h3>
      
      {/* Upcoming Bookings */}
      {upcomingBookings.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-medium text-slate-500 uppercase mb-2">{t('bookings.upcoming')}</p>
          <div className="space-y-3">
            {upcomingBookings.map((booking) => {
              const CallMethodIcon = CALL_METHOD_ICONS[booking.call_method] || Video;
              const dateTime = formatDateTime(booking.start_time_utc);
              
              return (
                <div
                  key={booking.id}
                  className="p-4 bg-aviation/5 border border-aviation/20 rounded-xl"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-travel-navy">{booking.agent_name}</p>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${STATUS_COLORS[booking.status]}`}>
                        {t(`bookings.status.${booking.status}`)}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-aviation">${booking.amount}</p>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-slate-600 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {dateTime.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {dateTime.time}
                    </span>
                    <span className="flex items-center gap-1">
                      <CallMethodIcon className="w-4 h-4" />
                      {booking.duration}m
                    </span>
                  </div>
                  
                  {booking.meeting_link && booking.payment_status === 'paid' && (
                    <a
                      href={booking.meeting_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-aviation text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-sky-600 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {t('bookings.joinCall')}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Past Bookings */}
      {pastBookings.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase mb-2">{t('bookings.past')}</p>
          <div className="space-y-2">
            {pastBookings.slice(0, 3).map((booking) => {
              const dateTime = formatDateTime(booking.start_time_utc);
              
              return (
                <div
                  key={booking.id}
                  className="p-3 bg-slate-50 rounded-xl flex items-center justify-between"
                >
                  <div>
                    <p className="font-medium text-travel-navy text-sm">{booking.agent_name}</p>
                    <p className="text-xs text-slate-500">{dateTime.date} • {booking.duration}m</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[booking.status]}`}>
                    {t(`bookings.status.${booking.status}`)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <button
        onClick={() => navigate('/agents')}
        className="mt-4 w-full text-aviation text-sm font-medium hover:underline flex items-center justify-center gap-1"
      >
        {t('bookings.bookAnother')}
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

export default MyBookings;
